import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { ARE_YOU_THERE_MESSAGE, IdleSessionService } from './core/auth/idle-session.service';
import { SessionExpiryService } from './core/auth/session-expiry.service';
import { SessionTimeoutService } from './core/auth/session-timeout.service';
import {
  STUDENT_OFFLINE_MESSAGE,
  TEACHER_OFFLINE_MESSAGE,
} from './core/services/connectivity/connectivity.constants';
import { ConnectivityService } from './core/services/connectivity/connectivity.service';
import { ProductConfigService } from './core/services/product-config/product-config.service';
import { SessionSupersededService } from './core/services/session-superseded/session-superseded.service';
import { ClassStore } from './core/stores/class.store';
import { UserStore } from './core/stores/user.store';
import { DIALOG_BUTTONS } from './shared/components/cv-dialog/cv-dialog.constants';
import { CvDialogComponent } from './shared/components/cv-dialog/cv-dialog.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CvDialogComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  host: {
    '[class]': 'themeClass()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly productConfig = inject(ProductConfigService);
  private readonly userStore = inject(UserStore);
  private readonly classStore = inject(ClassStore);
  private readonly sessionExpiry = inject(SessionExpiryService);
  private readonly sessionSuperseded = inject(SessionSupersededService);
  private readonly sessionTimeout = inject(SessionTimeoutService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly idleSession = inject(IdleSessionService);

  protected readonly themeClass = this.productConfig.themeClass;

  private readonly isStudent = computed(
    () => this.userStore.isStudent() || this.classStore.isStudent(),
  );
  private readonly offlineAcknowledged = signal(false);

  /**
   * The one alert on screen. First match wins: a superseded session outranks everything
   * because that device is finished, and an offline device cannot act on anything else.
   */
  protected readonly activeAlert = computed(() => {
    if (this.sessionSuperseded.superseded()) {
      return {
        message: this.sessionSuperseded.message(),
        buttons: DIALOG_BUTTONS.LOG_OUT_ONLY,
        maxPriority: true,
        confirm: () => this.sessionSuperseded.acknowledge(),
      };
    }
    if (this.sessionExpiry.expired()) {
      return {
        message: this.sessionExpiry.message(),
        buttons: DIALOG_BUTTONS.OK_ONLY,
        maxPriority: false,
        confirm: () => this.sessionExpiry.acknowledge(),
      };
    }
    if (!this.connectivity.isOnline() && !this.offlineAcknowledged()) {
      return {
        // Students cannot dismiss it: the alert is their only warning that work may be lost.
        message: this.isStudent() ? STUDENT_OFFLINE_MESSAGE : TEACHER_OFFLINE_MESSAGE,
        buttons: this.isStudent() ? DIALOG_BUTTONS.NO_BUTTONS : DIALOG_BUTTONS.OK_ONLY,
        maxPriority: false,
        confirm: () => this.offlineAcknowledged.set(true),
      };
    }
    if (this.idleSession.warningVisible()) {
      return {
        message: ARE_YOU_THERE_MESSAGE,
        buttons: DIALOG_BUTTONS.YES_ONLY,
        maxPriority: false,
        confirm: () => this.idleSession.confirmStillThere(),
      };
    }
    return null;
  });

  constructor() {
    this.sessionTimeout.start();
    this.idleSession.start();

    effect(() => {
      if (this.connectivity.isOnline()) this.offlineAcknowledged.set(false);
    });
  }
}
